import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from '@angular/fire/app';
import {
  clearIndexedDbPersistence,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  terminate,
} from '@angular/fire/firestore';

const RECOVERY_FLAG_KEY = 'firestore-cache-recovery';
const RECOVERY_ATTEMPTS_KEY = 'firestore-cache-recovery-attempts';
const MAX_RECOVERY_ATTEMPTS = 2;

export function createFirestoreLocalCacheSettings() {
  return persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  });
}

export function getOrInitializeApp(firebaseConfig: FirebaseOptions): FirebaseApp {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export function isFirestoreIndexedDbError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const code = (error as { code?: string })?.code;

  return (
    code === 'failed-precondition' ||
    message.includes('IndexedDbTransactionError') ||
    message.includes('IndexedDB') ||
    message.includes('NotFoundError') ||
    message.includes('object stores was not found') ||
    message.includes('refusing to open IndexedDB') ||
    message.includes('Failed to persist write')
  );
}

export function requestFirestoreCacheRecovery(error: unknown): boolean {
  if (!isFirestoreIndexedDbError(error)) {
    return false;
  }

  const attempts = Number(sessionStorage.getItem(RECOVERY_ATTEMPTS_KEY) ?? '0');
  if (attempts >= MAX_RECOVERY_ATTEMPTS) {
    return false;
  }

  sessionStorage.setItem(RECOVERY_ATTEMPTS_KEY, String(attempts + 1));
  sessionStorage.setItem(RECOVERY_FLAG_KEY, '1');
  console.warn('IndexedDB structure broken. Clearing Firestore persistence cache on next load...');
  window.location.reload();

  return true;
}

export async function prepareFirestoreCache(firebaseConfig: FirebaseOptions): Promise<void> {
  if (sessionStorage.getItem(RECOVERY_FLAG_KEY) !== '1') {
    return;
  }

  sessionStorage.removeItem(RECOVERY_FLAG_KEY);

  const app = getOrInitializeApp(firebaseConfig);
  const firestore = initializeFirestore(app, {
    localCache: createFirestoreLocalCacheSettings(),
  });

  await terminate(firestore);
  await clearIndexedDbPersistence(firestore);
  sessionStorage.removeItem(RECOVERY_ATTEMPTS_KEY);
}

export function registerFirestoreCacheRecoveryHandler(): void {
  window.addEventListener('unhandledrejection', (event) => {
    if (requestFirestoreCacheRecovery(event.reason)) {
      event.preventDefault();
    }
  });
}
